import { TradingLevels, PineScriptConfig } from '../types/trading';
import { PINE_SCRIPT_TEMPLATE } from '../constants/pineScriptTemplate';

/**
 * Maps trading levels to Pine Script configuration values with improved logic
 */
export function mapTradingLevelsToPineScript(levels: TradingLevels): PineScriptConfig {
  const { supportLevels, resistanceLevels, swingHigh, swingLow } = levels;

  // Ensure we have swing points
  const finalSwingHigh = swingHigh || (resistanceLevels.length > 0 ? Math.max(...resistanceLevels) : 0);
  const finalSwingLow = swingLow || (supportLevels.length > 0 ? Math.min(...supportLevels) : 0);

  // Sort resistance levels descending - include ALL levels
  const allResistanceLevels = [...resistanceLevels].sort((a, b) => b - a);

  // Map resistance levels intelligently - distribute ALL extracted levels
  const majorResistance = allResistanceLevels.length > 0 ? allResistanceLevels[0] : finalSwingHigh;
  
  // Use swing high as one of the levels if it's different from major resistance
  const resistanceRecoveryHigh = majorResistance !== finalSwingHigh ? finalSwingHigh :
                                 allResistanceLevels.length > 1 ? allResistanceLevels[1] :
                                 finalSwingHigh * 0.98;
  
  const resistanceRecoveryLow = allResistanceLevels.length > 2 ? allResistanceLevels[2] :
                                allResistanceLevels.length > 1 ? allResistanceLevels[1] :
                                resistanceRecoveryHigh * 0.98;
  
  const minorResistance = allResistanceLevels.length > 3 ? allResistanceLevels[3] :
                          allResistanceLevels.length > 2 ? allResistanceLevels[2] :
                          resistanceRecoveryLow * 0.98;

  // Sort support levels descending - include ALL levels but filter out swing high
  const validSupportLevels = supportLevels
    .filter(level => level < finalSwingHigh) // Exclude any support >= swing high
    .sort((a, b) => b - a);
  const allSupportLevels = validSupportLevels;

  // Map support levels intelligently (highest support first) - distribute ALL extracted levels
  const immediateSupport = allSupportLevels.length > 0 ? allSupportLevels[0] :
                          finalSwingLow * 1.02;
  
  const majorSupport1 = allSupportLevels.length > 1 ? allSupportLevels[1] :
                        finalSwingLow;
  
  const majorSupport2 = allSupportLevels.length > 2 ? allSupportLevels[2] :
                        allSupportLevels.length > 1 ? allSupportLevels[1] :
                        finalSwingLow * 0.98;

  // Additional levels for expanded template
  const resistanceLevel5 = allResistanceLevels.length > 4 ? allResistanceLevels[4] :
                           allResistanceLevels.length > 3 ? allResistanceLevels[3] :
                           minorResistance * 0.98;
  
  const resistanceLevel6 = allResistanceLevels.length > 5 ? allResistanceLevels[5] :
                           allResistanceLevels.length > 4 ? allResistanceLevels[4] :
                           resistanceLevel5 * 0.98;

  const supportLevel4 = allSupportLevels.length > 3 ? allSupportLevels[3] :
                        allSupportLevels.length > 2 ? allSupportLevels[2] :
                        majorSupport2 * 0.98;

  return {
    swingHigh: finalSwingHigh,
    swingLow: finalSwingLow,
    majorResistance,
    resistanceRecoveryHigh,
    resistanceRecoveryLow,
    minorResistance,
    resistanceLevel5,
    resistanceLevel6,
    immediateSupport,
    majorSupport1,
    majorSupport2,
    supportLevel4
  };
}

/**
 * Replaces template placeholders with actual values
 */
export function replaceTemplateValues(template: string, values: Record<string, number>): string {
  let result = template;
  
  // Replace each placeholder with its corresponding value
  Object.entries(values).forEach(([key, value]) => {
    const placeholder = `{{${key}}}`;
    const formattedValue = formatNumberForPineScript(value);
    result = result.replace(new RegExp(placeholder, 'g'), formattedValue);
  });
  
  return result;
}

/**
 * Formats numbers appropriately for Pine Script
 */
export function formatNumberForPineScript(value: number): string {
  // For very small values (forex, some crypto), keep more decimal places
  if (value < 1) {
    return value.toFixed(6);
  }
  // For most stock/crypto prices, 2 decimal places are sufficient
  else if (value < 1000) {
    return value.toFixed(2);
  }
  // For larger values, use appropriate precision
  else {
    return value.toFixed(2);
  }
}

/**
 * Validates trading levels before Pine Script generation
 */
export function validateTradingLevelsBeforeMapping(levels: TradingLevels): { isValid: boolean; warnings: string[]; errors: string[] } {
  const warnings: string[] = [];
  const errors: string[] = [];
  
  // Check swing points
  if (!levels.swingHigh || !levels.swingLow) {
    errors.push('Missing swing high or swing low values');
  } else if (levels.swingHigh <= levels.swingLow) {
    errors.push(`Invalid swing points: High (${levels.swingHigh}) must be greater than Low (${levels.swingLow})`);
  }
  
  // Check for insufficient levels
  if (levels.supportLevels.length === 0) {
    warnings.push('No support levels detected - using fallback values');
  }
  
  if (levels.resistanceLevels.length === 0) {
    warnings.push('No resistance levels detected - using fallback values');
  }
  
  // Check for missing key levels
  const allResistance = [...levels.resistanceLevels];
  if (levels.swingHigh) allResistance.push(levels.swingHigh);
  const highestResistance = Math.max(...allResistance);
  
  if (levels.resistanceLevels.length > 0 && !levels.resistanceLevels.includes(highestResistance)) {
    warnings.push(`Highest resistance level (${highestResistance}) may not be properly captured`);
  }
  
  // Check for logical inconsistencies
  const range = levels.swingHigh - levels.swingLow;
  if (range < 1) {
    warnings.push(`Very small price range (${range.toFixed(2)}) - levels may be too close together`);
  }
  
  return {
    isValid: errors.length === 0,
    warnings,
    errors
  };
}

/**
 * Validates Pine Script configuration before template replacement
 */
export function validatePineScriptConfig(config: PineScriptConfig): { isValid: boolean; warnings: string[]; errors: string[] } {
  const warnings: string[] = [];
  const errors: string[] = [];
  
  // Check resistance level hierarchy (more flexible - allow for market structure variations)
  const resistanceTolerance = Math.abs(config.swingHigh - config.swingLow) * 0.02; // 2% of range tolerance
  
  if (config.majorResistance < config.resistanceRecoveryHigh - resistanceTolerance) {
    warnings.push(`Major resistance (${config.majorResistance}) is below recovery high (${config.resistanceRecoveryHigh})`);
  }
  
  if (config.resistanceRecoveryHigh < config.resistanceRecoveryLow - resistanceTolerance) {
    warnings.push(`Recovery high (${config.resistanceRecoveryHigh}) is below recovery low (${config.resistanceRecoveryLow})`);
  }
  
  // Check support level hierarchy (more flexible - allow for market structure variations)
  // Only flag major discrepancies, not minor variations
  const supportTolerance = Math.abs(config.swingHigh - config.swingLow) * 0.05; // 5% of range tolerance
  
  if (config.immediateSupport < config.majorSupport1 - supportTolerance) {
    warnings.push(`Immediate support (${config.immediateSupport}) is significantly below major support 1 (${config.majorSupport1})`);
  }
  
  if (config.majorSupport1 < config.majorSupport2 - supportTolerance) {
    warnings.push(`Major support 1 (${config.majorSupport1}) is significantly below major support 2 (${config.majorSupport2})`);
  }
  
  // Check swing point integrity
  if (config.swingHigh <= config.swingLow) {
    errors.push(`Swing high (${config.swingHigh}) must be > swing low (${config.swingLow})`);
  }
  
  // Check for potential issues
  if (config.majorResistance === config.resistanceRecoveryHigh) {
    warnings.push('Major resistance and recovery high are identical - may lack granularity');
  }
  
  if (config.immediateSupport === config.majorSupport1) {
    warnings.push('Immediate support and major support 1 are identical - may lack granularity');
  }
  
  return {
    isValid: errors.length === 0,
    warnings,
    errors
  };
}

/**
 * Validates that the Pine Script has valid syntax
 */
export function validatePineScriptSyntax(script: string): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  // Check for remaining unreplaced placeholders
  const placeholderPattern = /\{\{[A-Z_]+\}\}/g;
  const unreplacedPlaceholders = script.match(placeholderPattern);
  if (unreplacedPlaceholders) {
    errors.push(`Unreplaced placeholders found: ${unreplacedPlaceholders.join(', ')}`);
  }
  
  // Check for basic Pine Script syntax requirements
  if (!script.includes('//@version=5')) {
    errors.push('Missing Pine Script version declaration');
  }
  
  if (!script.includes('indicator(')) {
    errors.push('Missing indicator declaration');
  }
  
  // Check for impossible values (swing high should be > swing low)
  const swingHighMatch = script.match(/swing_high = input\.float\(([0-9.]+)/);
  const swingLowMatch = script.match(/swing_low = input\.float\(([0-9.]+)/);
  
  if (swingHighMatch && swingLowMatch) {
    const swingHigh = parseFloat(swingHighMatch[1]);
    const swingLow = parseFloat(swingLowMatch[1]);
    
    if (swingHigh <= swingLow) {
      errors.push(`Invalid swing points: High (${swingHigh}) must be greater than Low (${swingLow})`);
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Generates Pine Script info table with dynamic content based on extracted levels
 */
export function generateInfoTableContent(config: PineScriptConfig): string {
  const range = config.swingHigh - config.swingLow;
  
  return `
    table.cell(infoTable, 0, 1, "Swing Range (${range.toFixed(0)}pts)", text_color=color.white)
    table.cell(infoTable, 1, 1, str.tostring(fib_range, "#.##"), text_color=color.white)`;
}

/**
 * Formats AI response text as Pine Script comments
 */
function formatAIResponseAsComments(aiResponseText: string): string {
  if (!aiResponseText || aiResponseText.trim().length === 0) {
    return '';
  }

  const lines = aiResponseText.trim().split('\n');
  const maxLineLength = 80;
  const formattedLines: string[] = [];
  
  // Add header
  formattedLines.push('');
  formattedLines.push('// ==========================================');
  formattedLines.push('// AI TRADING ANALYSIS REFERENCE');
  formattedLines.push('// ==========================================');
  formattedLines.push('//');
  
  // Process each line
  lines.forEach(line => {
    const trimmedLine = line.trim();
    if (trimmedLine.length === 0) {
      formattedLines.push('//');
      return;
    }
    
    // Break long lines into multiple comment lines
    if (trimmedLine.length <= maxLineLength - 3) {
      formattedLines.push(`// ${trimmedLine}`);
    } else {
      const words = trimmedLine.split(' ');
      let currentLine = '';
      
      words.forEach(word => {
        if (currentLine.length + word.length + 1 <= maxLineLength - 3) {
          currentLine += (currentLine ? ' ' : '') + word;
        } else {
          if (currentLine) {
            formattedLines.push(`// ${currentLine}`);
          }
          currentLine = word;
        }
      });
      
      if (currentLine) {
        formattedLines.push(`// ${currentLine}`);
      }
    }
  });
  
  // Add footer
  formattedLines.push('//');
  formattedLines.push('// ==========================================');
  formattedLines.push('');
  
  return formattedLines.join('\n');
}

/**
 * Main function to generate complete Pine Script from trading levels with comprehensive validation
 */
export function generatePineScript(
  levels: TradingLevels, 
  aiResponseText?: string
): { 
  script: string; 
  validation: { isValid: boolean; warnings: string[]; errors: string[] } 
} {
  // Step 1: Validate input trading levels
  const levelValidation = validateTradingLevelsBeforeMapping(levels);
  
  if (!levelValidation.isValid) {
    return {
      script: '',
      validation: {
        isValid: false,
        warnings: levelValidation.warnings,
        errors: ['Trading level validation failed:', ...levelValidation.errors]
      }
    };
  }
  
  // Step 2: Map trading levels to Pine Script configuration
  const config = mapTradingLevelsToPineScript(levels);
  
  // Step 3: Validate the configuration
  const configValidation = validatePineScriptConfig(config);
  
  if (!configValidation.isValid) {
    return {
      script: '',
      validation: {
        isValid: false,
        warnings: [...levelValidation.warnings, ...configValidation.warnings],
        errors: ['Pine Script configuration validation failed:', ...configValidation.errors]
      }
    };
  }
  
  // Step 4: Create replacement values object
  const replacementValues = {
    SWING_HIGH: config.swingHigh,
    SWING_LOW: config.swingLow,
    MAJOR_RESISTANCE: config.majorResistance,
    RESISTANCE_RECOVERY_HIGH: config.resistanceRecoveryHigh,
    RESISTANCE_RECOVERY_LOW: config.resistanceRecoveryLow,
    MINOR_RESISTANCE: config.minorResistance,
    RESISTANCE_LEVEL_5: config.resistanceLevel5,
    RESISTANCE_LEVEL_6: config.resistanceLevel6,
    IMMEDIATE_SUPPORT: config.immediateSupport,
    MAJOR_SUPPORT_1: config.majorSupport1,
    MAJOR_SUPPORT_2: config.majorSupport2,
    SUPPORT_LEVEL_4: config.supportLevel4,
    // Include AI-specified Fibonacci levels
    FIB_236: levels.fibonacciRetracements.level236,
    FIB_382: levels.fibonacciRetracements.level382,
    FIB_500: levels.fibonacciRetracements.level500,
    FIB_618: levels.fibonacciRetracements.level618,
    FIB_786: levels.fibonacciRetracements.level786,
    FIB_EXT_1272: levels.fibonacciExtensions.level1272,
    FIB_EXT_1618: levels.fibonacciExtensions.level1618
  };
  
  // Step 5: Replace template placeholders
  let generatedScript = replaceTemplateValues(PINE_SCRIPT_TEMPLATE, replacementValues);
  
  // Step 6: Add AI response as comments (if provided)
  if (aiResponseText && aiResponseText.trim().length > 0) {
    const aiComments = formatAIResponseAsComments(aiResponseText);
    // Insert AI comments after the Pine Script header but before the main code
    const scriptLines = generatedScript.split('\n');
    const insertIndex = scriptLines.findIndex(line => line.includes('indicator(')) + 1;
    
    if (insertIndex > 0) {
      scriptLines.splice(insertIndex, 0, aiComments);
      generatedScript = scriptLines.join('\n');
    } else {
      // Fallback: add at the beginning
      generatedScript = aiComments + '\n' + generatedScript;
    }
  }
  
  // Step 7: Validate the generated script syntax
  const syntaxValidation = validatePineScriptSyntax(generatedScript);
  
  const allWarnings = [...levelValidation.warnings, ...configValidation.warnings];
  const allErrors = syntaxValidation.isValid ? [] : syntaxValidation.errors;
  
  return {
    script: generatedScript,
    validation: {
      isValid: syntaxValidation.isValid,
      warnings: allWarnings,
      errors: allErrors
    }
  };
}

/**
 * Generates a summary of the Pine Script configuration for user display
 */
export function generatePineScriptSummary(levels: TradingLevels): {
  summary: string;
  keyLevels: { name: string; value: number; type: 'support' | 'resistance' | 'fibonacci' }[];
} {
  const config = mapTradingLevelsToPineScript(levels);
  const range = config.swingHigh - config.swingLow;
  
  const keyLevels = [
    { name: 'Swing High', value: config.swingHigh, type: 'resistance' as const },
    { name: 'Swing Low', value: config.swingLow, type: 'support' as const },
    { name: 'Major Resistance', value: config.majorResistance, type: 'resistance' as const },
    { name: 'Recovery High', value: config.resistanceRecoveryHigh, type: 'resistance' as const },
    { name: 'Immediate Support', value: config.immediateSupport, type: 'support' as const },
    { name: 'Major Support', value: config.majorSupport1, type: 'support' as const },
    { name: 'Fib 38.2%', value: levels.fibonacciRetracements.level382, type: 'fibonacci' as const },
    { name: 'Fib 61.8%', value: levels.fibonacciRetracements.level618, type: 'fibonacci' as const }
  ];
  
  const summary = `Pine Script generated with ${levels.supportLevels.length} support levels, ${levels.resistanceLevels.length} resistance levels, and complete Fibonacci analysis. Range: ${range.toFixed(2)} points.`;
  
  return { summary, keyLevels };
}